import {
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    DataTable,
    DropZone,
    FormLayout,
    InlineStack,
    Layout,
    Modal,
    Page,
    ProgressBar,
    Select,
    Text,
    TextField,
    Thumbnail,
} from '@shopify/polaris';
import { NoteIcon } from '@shopify/polaris-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiErrorMessage } from '../../app/api/error';
import agent, { AcesImportSetup, AtdPriceAdjustmentMode, CsvXlsxImportMode, CsvXlsxImportPreview, FitmentImportHistoryItem, ImportFormat, ProductTagFormatPreset, ProductTagImportPreview, ProductTagValidationMode } from '../../app/api/agent';
import { AtdProfile } from '../../app/models/Atd';
import ImportHistoryCards from './ImportHistoryCards';

type UploadState = {
    file?: File;
    error?: string;
    success?: string;
};

type AcesSetupState = {
    queueId: number;
    details: AcesImportSetup;
    selectedVendor: string;
    skuSuffixRule: string;
    useSkuSuffixRule: boolean;
    updateVendorRule: boolean;
    normalizeSku: boolean;
};

type VendorSkuRuleSettings = {
    skuPrefix: string;
    normalizeSku: boolean;
};

type ImportsPageView = 'imports' | 'exports';

const noVendorOptionValue = '__NO_VENDOR__';

const baseFormatOptions = [
    { label: 'CSV/XLSX', value: 'csv_xlsx' },
    { label: 'ACES XML', value: 'aces_xml' },
];

const atdPriceFormatOption = { label: 'ATD Price File', value: 'atd_price_file' };
const productTagsFormatOption = { label: 'Product Tags', value: 'product_tags' };

const priceAdjustmentOptions = [
    { label: 'No adjustment', value: 'none' },
    { label: 'Increase by percentage', value: 'percent' },
    { label: 'Increase by fixed amount', value: 'fixed' },
];

const csvXlsxImportModeOptions = [
    { label: 'Append to existing fitments', value: 'append' },
    { label: 'Replace fitments for SKUs in this file', value: 'replace_matched_skus' },
];

const productTagFormatOptions = [
    { label: 'App default tag format', value: 'app_default' },
    { label: 'Turn14 fits_ tag', value: 'turn14' },
    { label: 'Make-Model-Year', value: 'make_model_year' },
    { label: 'Make-Model-Submodel-Year', value: 'make_model_submodel_year' },
    { label: 'Year-Make-Model', value: 'year_make_model' },
    { label: 'Custom pattern', value: 'custom' },
];

const productTagValidationOptions = [
    { label: 'Create missing vehicle values', value: 'create_missing' },
    { label: 'Only import existing vehicle values', value: 'existing_only' },
];

const importsPageViewOptions = [
    { value: 'imports' as ImportsPageView, label: 'Imports' },
    { value: 'exports' as ImportsPageView, label: 'Exports' },
];

const segmentedToggleStyle: React.CSSProperties = {
    display: 'inline-flex',
    gap: 2,
    padding: 3,
    border: '1px solid #d0d5dd',
    borderRadius: 14,
    background: '#f7f7f7',
};

const getSegmentedToggleButtonStyle = (isSelected: boolean): React.CSSProperties => ({
    minHeight: 34,
    padding: '6px 18px',
    border: '1px solid transparent',
    borderRadius: 11,
    background: isSelected ? '#fff' : 'transparent',
    boxShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.18)' : 'none',
    color: '#202223',
    cursor: 'pointer',
    font: 'inherit',
    fontWeight: isSelected ? 600 : 400,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
});

type ImportsExportsToggleProps = {
    value: ImportsPageView;
    onChange: (value: ImportsPageView) => void;
};

const ImportsExportsToggle = ({ value, onChange }: ImportsExportsToggleProps) => (
    <div
        aria-label="Imports and exports view"
        role="group"
        style={segmentedToggleStyle}
    >
        {importsPageViewOptions.map((option) => {
            const isSelected = value === option.value;

            return (
                <button
                    key={option.value}
                    type="button"
                    aria-pressed={isSelected}
                    onClick={() => onChange(option.value)}
                    style={getSegmentedToggleButtonStyle(isSelected)}
                >
                    {option.label}
                </button>
            );
        })}
    </div>
);

const previewTableWrapperStyle: React.CSSProperties = {
    overflowX: 'auto',
};

const previewTableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
};

const previewTableCellStyle: React.CSSProperties = {
    padding: '12px 16px',
    borderBottom: '1px solid var(--p-color-border-secondary)',
    textAlign: 'left',
    verticalAlign: 'top',
    whiteSpace: 'nowrap',
};

type CsvPreviewTableProps = {
    headings: string[];
    rows: string[][];
};

const CsvPreviewTable = ({ headings, rows }: CsvPreviewTableProps) => (
    <div style={previewTableWrapperStyle}>
        <table style={previewTableStyle}>
            <thead>
                <tr>
                    {headings.map((heading) => (
                        <th key={heading} scope="col" style={previewTableCellStyle}>
                            <Text as="span" variant="bodyMd" fontWeight="regular">{heading}</Text>
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {rows.map((row, rowIndex) => (
                    <tr key={`csv-preview-row-${rowIndex}`}>
                        {row.map((cell, cellIndex) => (
                            <td key={`csv-preview-cell-${rowIndex}-${cellIndex}`} style={previewTableCellStyle}>
                                {cell || '-'}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    </div>
);

const hasConnectedAtdPriceProfile = (profile: AtdProfile) => {
    if (!profile?.isActive) {
        return false;
    }

    // The profile endpoint returns a default unsaved profile, so require the
    // standard API credential fields that indicate a merchant actually connected ATD.
    return !!(
        profile.standardBaseUrl?.trim() &&
        profile.clientId?.trim() &&
        profile.username?.trim() &&
        profile.password?.trim() &&
        profile.locationNumber?.trim()
    );
};

const ImportsPage = () => {
    const [pageView, setPageView] = useState<ImportsPageView>('imports');
    const [importFormat, setImportFormat] = useState<ImportFormat>('csv_xlsx');
    const [uploadState, setUploadState] = useState<UploadState>({});
    const [exportState, setExportState] = useState<Pick<UploadState, 'error' | 'success'>>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgressPercent, setUploadProgressPercent] = useState<number | undefined>();
    const [history, setHistory] = useState<FitmentImportHistoryItem[]>([]);
    const [historyError, setHistoryError] = useState<string | undefined>();
    const [historyErrorTitle, setHistoryErrorTitle] = useState<string | undefined>();
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [archivingIds, setArchivingIds] = useState<Record<number, boolean>>({});
    const [downloadingIds, setDownloadingIds] = useState<Record<number, boolean>>({});
    const [downloadingReviewIds, setDownloadingReviewIds] = useState<Record<number, boolean>>({});
    const [isConfirmingAcesSetup, setIsConfirmingAcesSetup] = useState(false);
    const [acesSetup, setAcesSetup] = useState<AcesSetupState | undefined>();
    const [vendorSuffixRulesByVendor, setVendorSuffixRulesByVendor] = useState<Record<string, VendorSkuRuleSettings>>({});
    const [canUseAtdPriceFile, setCanUseAtdPriceFile] = useState(false);
    const [priceAdjustmentMode, setPriceAdjustmentMode] = useState<AtdPriceAdjustmentMode>('none');
    const [priceAdjustmentValue, setPriceAdjustmentValue] = useState('');
    const [skipZeroMapPrices, setSkipZeroMapPrices] = useState(true);
    const [productTagFormatPreset, setProductTagFormatPreset] = useState<ProductTagFormatPreset>('app_default');
    const [productTagCustomFormat, setProductTagCustomFormat] = useState('{make}-{model}-{year}');
    const [productTagValidationMode, setProductTagValidationMode] = useState<ProductTagValidationMode>('create_missing');
    const [productTagPreview, setProductTagPreview] = useState<ProductTagImportPreview | undefined>();
    const [productTagPreviewError, setProductTagPreviewError] = useState<string | undefined>();
    const [reviewingProductTagIds, setReviewingProductTagIds] = useState<Record<number, boolean>>({});
    const [approvingProductTagIds, setApprovingProductTagIds] = useState<Record<number, boolean>>({});
    const [undoingProductTagIds, setUndoingProductTagIds] = useState<Record<number, boolean>>({});
    const [csvPreview, setCsvPreview] = useState<CsvXlsxImportPreview | undefined>();
    const [csvPreviewFileKey, setCsvPreviewFileKey] = useState<string | undefined>();
    const [isCsvPreviewModalOpen, setIsCsvPreviewModalOpen] = useState(false);
    const [isPreviewingCsv, setIsPreviewingCsv] = useState(false);
    const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
    const [isQueueingExport, setIsQueueingExport] = useState(false);
    const [csvXlsxImportMode, setCsvXlsxImportMode] = useState<CsvXlsxImportMode>('append');

    const formatOptions = useMemo(() => {
        return canUseAtdPriceFile
            ? [...baseFormatOptions, atdPriceFormatOption, productTagsFormatOption]
            : [...baseFormatOptions, productTagsFormatOption];
    }, [canUseAtdPriceFile]);

    const isValidFileForFormat = useCallback((file: File, format: ImportFormat) => {
        const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() : '';

        if (format === 'product_tags') {
            return true;
        }

        if (format === 'csv_xlsx' || format === 'atd_price_file') {
            return extension === 'csv' || extension === 'xlsx';
        }

        return extension === 'xml';
    }, []);

    const validationHint = useMemo(() => {
        if (importFormat === 'product_tags') {
            return 'No file required. The importer reads product tags from the synced Shopify product cache.';
        }

        return importFormat === 'csv_xlsx' || importFormat === 'atd_price_file'
            ? 'Accepted file types: .csv, .xlsx'
            : 'Accepted file types: .xml';
    }, [importFormat]);

    const getInvalidFileMessage = useCallback((format: ImportFormat) => {
        if (format === 'product_tags') {
            return 'Product Tags import does not require a file.';
        }

        if (format === 'aces_xml') {
            return 'Invalid file type. Select a .xml file.';
        }

        return 'Invalid file type. Select a .csv or .xlsx file.';
    }, []);

    const getFileKey = useCallback((file?: File) => {
        return file ? `${file.name}|${file.size}|${file.lastModified}` : undefined;
    }, []);

    const selectedFileKey = useMemo(() => getFileKey(uploadState.file), [getFileKey, uploadState.file]);
    // The preview must belong to the currently selected file; replacing the file
    // should always require a fresh mapping review before submission.
    const csvPreviewIsCurrent = importFormat === 'csv_xlsx' && !!csvPreview && csvPreviewFileKey === selectedFileKey;
    const csvPreviewCanSubmit = csvPreviewIsCurrent && csvPreview?.canImport === true;

    useEffect(() => {
        let isMounted = true;

        agent.Atd.profile()
            .then((profile) => {
                if (isMounted) {
                    setCanUseAtdPriceFile(hasConnectedAtdPriceProfile(profile));
                }
            })
            .catch(() => {
                if (isMounted) {
                    setCanUseAtdPriceFile(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!canUseAtdPriceFile && importFormat === 'atd_price_file') {
            setImportFormat('csv_xlsx');
        }
    }, [canUseAtdPriceFile, importFormat]);

    const loadHistory = useCallback(async () => {
        try {
            setIsLoadingHistory(true);
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            const result = await agent.Imports.history();
            setHistory(result);
        } catch (error: any) {
            setHistoryError(await getApiErrorMessage(error, 'Failed to load import history.'));
        } finally {
            setIsLoadingHistory(false);
        }
    }, []);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    useEffect(() => {
        if (!csvPreviewIsCurrent) {
            setIsCsvPreviewModalOpen(false);
        }
    }, [csvPreviewIsCurrent]);

    const loadVendorSuffixRules = useCallback(async () => {
        try {
            const rules = await agent.Settings.vendorSkuRules();
            const mappedRules = rules.reduce<Record<string, VendorSkuRuleSettings>>((accumulator, rule) => {
                accumulator[rule.shopifyVendor] = {
                    skuPrefix: rule.skuPrefix || '',
                    normalizeSku: rule.normalizeSku === true,
                };
                return accumulator;
            }, {});

            setVendorSuffixRulesByVendor(mappedRules);
        } catch {
            setVendorSuffixRulesByVendor({});
        }
    }, []);

    useEffect(() => {
        if (!acesSetup?.queueId) {
            return;
        }

        loadVendorSuffixRules();
    }, [acesSetup?.queueId, loadVendorSuffixRules]);

    useEffect(() => {
        if (!acesSetup?.selectedVendor) {
            return;
        }

        const vendorRule = vendorSuffixRulesByVendor[acesSetup.selectedVendor];

        if (vendorRule === undefined) {
            return;
        }

        setAcesSetup((previous) => {
            if (!previous || previous.selectedVendor !== acesSetup.selectedVendor || (previous.skuSuffixRule === vendorRule.skuPrefix && previous.normalizeSku === vendorRule.normalizeSku)) {
                return previous;
            }

            return {
                ...previous,
                skuSuffixRule: vendorRule.skuPrefix,
                normalizeSku: vendorRule.normalizeSku,
            };
        });
    }, [acesSetup?.selectedVendor, vendorSuffixRulesByVendor]);

    const handleDropZoneDrop = useCallback((_: File[], acceptedFiles: File[]) => {
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
        const selectedFile = acceptedFiles[0];

        if (!selectedFile) {
            setUploadState({ file: undefined, error: 'No file selected.' });
            return;
        }

        if (!isValidFileForFormat(selectedFile, importFormat)) {
            setUploadState({
                file: undefined,
                success: undefined,
                error: getInvalidFileMessage(importFormat)
            });
            return;
        }

        setUploadState({ file: selectedFile, error: undefined, success: undefined });
    }, [getInvalidFileMessage, importFormat, isValidFileForFormat]);

    const handleFormatChange = useCallback((value: string) => {
        const normalized = value as ImportFormat;
        setImportFormat(normalized);
        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);

        setUploadState((prev) => {
            if (normalized === 'product_tags') {
                return { file: undefined, error: undefined, success: undefined };
            }

            if (!prev.file) {
                return { ...prev, error: undefined };
            }

            if (isValidFileForFormat(prev.file, normalized)) {
                return { ...prev, error: undefined };
            }

            return {
                file: undefined,
                success: undefined,
                error: normalized === 'aces_xml'
                    ? 'Current file does not match ACES XML format. Please select a .xml file.'
                    : 'Current file does not match this format. Please select a .csv or .xlsx file.'
            };
        });
    }, [isValidFileForFormat]);

    const handleDownloadTemplate = useCallback(async () => {
        try {
            setIsDownloadingTemplate(true);
            const response = await agent.Imports.template();
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'fitment-import-template.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to download import template.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsDownloadingTemplate(false);
        }
    }, []);

    const handleQueueExport = useCallback(async () => {
        try {
            setIsQueueingExport(true);
            setExportState((prev) => ({ ...prev, error: undefined }));
            await agent.Imports.exportFitments();
            // Exports run through the history queue. The completed job exposes
            // the normal Download action once the worker has written the CSV.
            setExportState({
                error: undefined,
                success: 'Export started. The file will appear in export history when ready.'
            });
            await loadHistory();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to queue fitment export.');
            setExportState({ success: undefined, error: message });
        } finally {
            setIsQueueingExport(false);
        }
    }, [loadHistory]);

    const handlePreviewCsvXlsx = useCallback(async () => {
        if (!uploadState.file) {
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return false;
        }

        if (!isValidFileForFormat(uploadState.file, 'csv_xlsx')) {
            setUploadState((prev) => ({ ...prev, error: getInvalidFileMessage('csv_xlsx') }));
            return false;
        }

        try {
            setIsPreviewingCsv(true);
            setUploadState((prev) => ({ ...prev, error: undefined, success: undefined }));
            const preview = await agent.Imports.previewCsvXlsx(uploadState.file);
            setCsvPreview(preview);
            setCsvPreviewFileKey(getFileKey(uploadState.file));
            setIsCsvPreviewModalOpen(true);

            if (!preview.canImport) {
                setUploadState((prev) => ({ ...prev, error: 'Fix the CSV/XLSX column mapping before submitting this import.' }));
                return false;
            }

            return true;
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Unable to preview CSV/XLSX import.');
            setCsvPreview(undefined);
            setCsvPreviewFileKey(undefined);
            setIsCsvPreviewModalOpen(false);
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
            return false;
        } finally {
            setIsPreviewingCsv(false);
        }
    }, [getFileKey, getInvalidFileMessage, isValidFileForFormat, uploadState.file]);

    const handleSubmit = useCallback(async () => {
        if (importFormat !== 'product_tags' && !uploadState.file) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({ ...prev, error: 'Please select a file to upload.' }));
            return;
        }

        if (uploadState.file && !isValidFileForFormat(uploadState.file, importFormat)) {
            setUploadProgressPercent(undefined);
            setUploadState((prev) => ({
                ...prev,
                error: getInvalidFileMessage(importFormat)
            }));
            return;
        }

        if (importFormat === 'csv_xlsx' && !csvPreviewCanSubmit) {
            await handlePreviewCsvXlsx();
            return;
        }

        let parsedPriceAdjustmentValue: number | undefined;
        if (importFormat === 'atd_price_file' && priceAdjustmentMode !== 'none') {
            parsedPriceAdjustmentValue = Number(priceAdjustmentValue);

            if (!Number.isFinite(parsedPriceAdjustmentValue) || parsedPriceAdjustmentValue < 0) {
                setUploadProgressPercent(undefined);
                setUploadState((prev) => ({
                    ...prev,
                    error: 'Enter a price adjustment value greater than or equal to 0.'
                }));
                return;
            }
        }

        try {
            setIsSubmitting(true);
            setUploadProgressPercent(importFormat === 'product_tags' ? undefined : 0);
            const result = await agent.Imports.queue(
                uploadState.file,
                importFormat,
                (progressEvent) => {
                    if (!progressEvent.total || progressEvent.total <= 0) {
                        return;
                    }

                    const percent = Math.min(100, Math.round((progressEvent.loaded / progressEvent.total) * 100));
                    setUploadProgressPercent(percent);
                },
                importFormat === 'atd_price_file'
                    ? {
                        priceAdjustmentMode,
                        priceAdjustmentValue: parsedPriceAdjustmentValue,
                        skipZeroMapPrices,
                    }
                    : undefined,
                importFormat === 'product_tags'
                    ? {
                        productTagFormatPreset,
                        productTagCustomFormat: productTagFormatPreset === 'custom' ? productTagCustomFormat : undefined,
                        productTagValidationMode,
                    }
                    : undefined,
                importFormat === 'csv_xlsx'
                    ? {
                        csvXlsxImportMode,
                    }
                    : undefined
            );

            if (importFormat === 'aces_xml' && result.acesSetup) {
                const details = result.acesSetup;
                setAcesSetup({
                    queueId: result.queueId,
                    details,
                    selectedVendor: details.matchedVendor || '',
                    skuSuffixRule: details.vendorSkuSuffixRule || '',
                    useSkuSuffixRule: true,
                    updateVendorRule: true,
                    normalizeSku: details.normalizeSku === true,
                });

                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: 'ACES header parsed. Review vendor and SKU mapping settings, then start the import.'
                });
            } else {
                setUploadState({
                    file: undefined,
                    error: undefined,
                    success: importFormat === 'product_tags'
                        ? 'Product Tags preview has been scheduled. Review and apply it from import history when it is ready.'
                        : 'Your import has been scheduled for processing.'
                });
            }

            setCsvPreview(undefined);
            setCsvPreviewFileKey(undefined);
            setIsCsvPreviewModalOpen(false);
            await loadHistory();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to queue import.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsSubmitting(false);
            setUploadProgressPercent(undefined);
        }
    }, [csvPreviewCanSubmit, csvXlsxImportMode, getInvalidFileMessage, handlePreviewCsvXlsx, importFormat, isValidFileForFormat, loadHistory, priceAdjustmentMode, priceAdjustmentValue, productTagCustomFormat, productTagFormatPreset, productTagValidationMode, skipZeroMapPrices, uploadState.file]);

    const handlePrimaryImportAction = useCallback(() => {
        if (importFormat === 'csv_xlsx' && csvPreviewCanSubmit) {
            setIsCsvPreviewModalOpen(true);
            return;
        }

        handleSubmit();
    }, [csvPreviewCanSubmit, handleSubmit, importFormat]);

    const handleCloseCsvPreviewModal = useCallback(() => {
        if (isSubmitting) {
            return;
        }

        setIsCsvPreviewModalOpen(false);
    }, [isSubmitting]);

    const handleCancelCsvImport = useCallback(() => {
        if (isSubmitting) {
            return;
        }

        setUploadState({ file: undefined, error: undefined, success: undefined });
        setUploadProgressPercent(undefined);
        setCsvPreview(undefined);
        setCsvPreviewFileKey(undefined);
        setIsCsvPreviewModalOpen(false);
    }, [isSubmitting]);

    const handleConfirmAcesSetup = useCallback(async () => {
        if (!acesSetup) {
            return;
        }

        try {
            setIsConfirmingAcesSetup(true);
            const hasVendorSelection = !!acesSetup.selectedVendor;
            await agent.Imports.configureAces(acesSetup.queueId, {
                selectedVendor: hasVendorSelection ? acesSetup.selectedVendor : undefined,
                skuSuffixRule: hasVendorSelection ? acesSetup.skuSuffixRule : undefined,
                useSkuSuffixRule: hasVendorSelection && acesSetup.useSkuSuffixRule,
                updateVendorRule: hasVendorSelection && acesSetup.updateVendorRule,
                normalizeSku: hasVendorSelection && acesSetup.normalizeSku,
            });

            setUploadState({ file: undefined, error: undefined, success: 'ACES import configured and queued for processing.' });
            setAcesSetup(undefined);
            setUploadProgressPercent(undefined);
            await loadHistory();
        } catch (error: any) {
            const message = await getApiErrorMessage(error, 'Failed to configure ACES import.');
            setUploadState((prev) => ({ ...prev, success: undefined, error: message }));
        } finally {
            setIsConfirmingAcesSetup(false);
        }
    }, [acesSetup, loadHistory]);

    const handleCloseAcesModal = useCallback(() => {
        if (isConfirmingAcesSetup) {
            return;
        }

        setAcesSetup(undefined);
        setUploadProgressPercent(undefined);
    }, [isConfirmingAcesSetup]);

    const handleArchive = useCallback(async (queueId: number) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setArchivingIds((prev) => ({ ...prev, [queueId]: true }));
            await agent.Imports.archive(queueId);
            setHistory((prev) => prev.filter((item) => item.queueId !== queueId));
        } catch (error: any) {
            setHistoryErrorTitle('Unable to archive history item');
            setHistoryError(await getApiErrorMessage(error, 'Failed to archive import history item.'));
        } finally {
            setArchivingIds((prev) => ({ ...prev, [queueId]: false }));
        }
    }, []);

    const handleDownload = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.download(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = item.originalFileName || `import-${item.queueId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download import file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import file.'));
        } finally {
            setDownloadingIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleDownloadReview = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: true }));
            const response = await agent.Imports.downloadReview(item.queueId);
            const blobUrl = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            const baseName = (item.originalFileName || `import-${item.queueId}`).replace(/\.[^/.]+$/, '');
            link.href = blobUrl;
            link.download = `${baseName}-review.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to download review file');
            setHistoryError(await getApiErrorMessage(error, 'Failed to download import review file.'));
        } finally {
            setDownloadingReviewIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleReviewProductTags = useCallback(async (item: FitmentImportHistoryItem) => {
        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setProductTagPreviewError(undefined);
            setReviewingProductTagIds((prev) => ({ ...prev, [item.queueId]: true }));
            const preview = await agent.Imports.productTagsPreview(item.queueId);
            setProductTagPreview(preview);
        } catch (error: any) {
            setHistoryErrorTitle('Unable to load Product Tags preview');
            setHistoryError(await getApiErrorMessage(error, 'Failed to load Product Tags preview.'));
        } finally {
            setReviewingProductTagIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, []);

    const handleApproveProductTags = useCallback(async () => {
        if (!productTagPreview) {
            return;
        }

        try {
            setProductTagPreviewError(undefined);
            setApprovingProductTagIds((prev) => ({ ...prev, [productTagPreview.queueId]: true }));
            const result = await agent.Imports.approveProductTags(productTagPreview.queueId);
            setUploadState({
                file: undefined,
                error: undefined,
                success: `Applied ${result.appliedFitmentCount.toLocaleString()} fitment mapping(s) from the Product Tags preview.`
            });
            setProductTagPreview(undefined);
            await loadHistory();
        } catch (error: any) {
            setProductTagPreviewError(await getApiErrorMessage(error, 'Failed to approve Product Tags import.'));
        } finally {
            setApprovingProductTagIds((prev) => ({ ...prev, [productTagPreview.queueId]: false }));
        }
    }, [loadHistory, productTagPreview]);

    const handleUndoProductTags = useCallback(async (item: FitmentImportHistoryItem) => {
        if (!window.confirm('Undo this Product Tags import? Fitment mappings created by this import will be removed from the app source of truth.')) {
            return;
        }

        try {
            setHistoryError(undefined);
            setHistoryErrorTitle(undefined);
            setUndoingProductTagIds((prev) => ({ ...prev, [item.queueId]: true }));
            const result = await agent.Imports.undoProductTags(item.queueId);
            setUploadState({
                file: undefined,
                error: undefined,
                success: `Undid ${result.removedFitmentMappings.toLocaleString()} fitment mapping(s) from Product Tags import ${item.queueId}.`
            });
            await loadHistory();
        } catch (error: any) {
            setHistoryErrorTitle('Unable to undo Product Tags import');
            setHistoryError(await getApiErrorMessage(error, 'Failed to undo Product Tags import.'));
        } finally {
            setUndoingProductTagIds((prev) => ({ ...prev, [item.queueId]: false }));
        }
    }, [loadHistory]);

    const productTagPreviewRows = useMemo(() => {
        if (!productTagPreview) {
            return [];
        }

        return productTagPreview.rows.map((row) => {
            const fitment = row.isUniversal
                ? 'Universal product'
                : [row.year, row.make, row.model, row.submodel].filter(Boolean).join(' ');
            const action = row.isApplied
                ? 'Applied'
                : row.willApply
                    ? row.willCreateFitmentRecord
                        ? 'Create and apply'
                        : 'Apply'
                    : 'Skip';

            return [
                row.productTitle || `Product ${row.productId}`,
                row.productSku || '-',
                row.sourceTag,
                fitment || '-',
                action,
            ];
        });
    }, [productTagPreview]);

    const fileUpload = !uploadState.file && <DropZone.FileUpload actionHint={validationHint} />;
    const uploadedFile = uploadState.file && (
        <InlineStack gap="300" align="start" blockAlign="center">
            <Thumbnail
                size="small"
                alt={uploadState.file.name}
                source={NoteIcon}
            />
            <div>
                <Text as="p" variant="bodyMd">{uploadState.file.name}</Text>
                <Text as="p" variant="bodySm" tone="subdued">{uploadState.file.size} bytes</Text>
            </div>
        </InlineStack>
    );
    const csvPreviewRows = csvPreviewIsCurrent
        ? (csvPreview?.rows ?? []).map((row) => {
            const status = row.missingRequiredValues
                    ? 'Missing required value'
                    : row.emptyFitment
                        ? 'No fitment values'
                        : !row.sku
                            ? (row.productId || row.productHandle ? 'Product-level' : 'Vehicle only')
                            : 'Ready';

            return [
                String(row.rowNumber),
                ...row.cells,
                status,
            ];
        })
        : [];
    const csvPreviewHeadings = csvPreviewIsCurrent
        ? ['Row', ...(csvPreview?.headings ?? []), 'Status']
        : [];
    const csvColumnMappingRows = csvPreviewIsCurrent
        ? (csvPreview?.mappedColumns ?? []).map((column) => [
            column.role === 'SKU' ? 'SKU' : column.fieldLabel,
            column.columnIndex >= 0 ? column.columnName || `Column ${column.columnIndex + 1}` : 'Not found',
            column.required ? 'Required' : 'Optional',
        ])
        : [];
    const importHistory = useMemo(
        () => history.filter((item) => item.importFormat !== 'fitment_export'),
        [history]
    );
    const exportHistory = useMemo(
        () => history.filter((item) => item.importFormat === 'fitment_export'),
        [history]
    );
    const visibleHistory = pageView === 'exports' ? exportHistory : importHistory;
    const historyTitle = pageView === 'exports' ? 'Export history' : 'Import history';
    const loadHistoryErrorTitle = pageView === 'exports' ? 'Unable to load export history' : 'Unable to load import history';
    const emptyHistoryText = pageView === 'exports' ? 'No export history yet.' : 'No import history yet.';

    return (
        <Page
            title={pageView === 'exports' ? 'Exports' : 'Imports'}
            secondaryActions={<ImportsExportsToggle value={pageView} onChange={setPageView} />}
        >
            <Layout>
                <Layout.Section>
                    {pageView === 'imports' ? (
                    <Card>
                        <FormLayout>
                            <Select
                                label="Import format"
                                options={formatOptions}
                                onChange={handleFormatChange}
                                value={importFormat}
                            />

                            {importFormat !== 'product_tags' ? (
                                <BlockStack gap="300">
                                    {importFormat === 'csv_xlsx' ? (
                                        <BlockStack gap="300">
                                            <InlineStack align="space-between" blockAlign="center">
                                                <Text as="p" tone="subdued">Download a template that matches your current fitment fields.</Text>
                                                <Button onClick={handleDownloadTemplate} loading={isDownloadingTemplate}>Download CSV template</Button>
                                            </InlineStack>

                                            <div style={{ borderTop: '1px solid var(--p-color-border-secondary)', paddingTop: 16 }}>
                                                <Select
                                                    label="Import mode"
                                                    options={csvXlsxImportModeOptions}
                                                    value={csvXlsxImportMode}
                                                    onChange={(value) => setCsvXlsxImportMode(value as CsvXlsxImportMode)}
                                                    helpText="Replace mode clears existing fitments only for variants whose SKU is matched by this file."
                                                />
                                            </div>
                                        </BlockStack>
                                    ) : null}

                                    <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                                        {uploadedFile}
                                        {fileUpload}
                                    </DropZone>

                                    {importFormat === 'csv_xlsx' && csvPreviewIsCurrent ? (
                                        <Banner tone={csvPreview?.canImport ? 'success' : 'warning'} title={csvPreview?.canImport ? 'CSV/XLSX preview ready' : 'CSV/XLSX preview needs attention'}>
                                            <BlockStack gap="200">
                                                <p>{csvPreview?.canImport ? 'The file has been scanned. Review the preview before submitting.' : 'The file was scanned, but required columns are missing.'}</p>
                                                <InlineStack align="start" gap="200">
                                                    <Button onClick={() => setIsCsvPreviewModalOpen(true)}>
                                                        Review preview
                                                    </Button>
                                                    <Button variant="plain" tone="critical" onClick={handleCancelCsvImport} disabled={isSubmitting}>
                                                        Cancel import
                                                    </Button>
                                                </InlineStack>
                                            </BlockStack>
                                        </Banner>
                                    ) : null}
                                </BlockStack>
                            ) : (
                                <BlockStack gap="300">
                                    <Banner tone="info" title="Import from Shopify product tags">
                                        <p>This reads existing product tags from the synced Shopify product cache and prepares a preview. App fitment mappings are not changed until you approve the preview.</p>
                                    </Banner>

                                    <Select
                                        label="Tag format"
                                        options={productTagFormatOptions}
                                        value={productTagFormatPreset}
                                        onChange={(value) => setProductTagFormatPreset(value as ProductTagFormatPreset)}
                                        helpText="Use the app default unless the products were tagged by another fitment app or supplier feed."
                                    />

                                    {productTagFormatPreset === 'custom' && (
                                        <TextField
                                            label="Custom tag pattern"
                                            value={productTagCustomFormat}
                                            onChange={setProductTagCustomFormat}
                                            autoComplete="off"
                                            helpText="Use tokens like {make}, {model}, {submodel}, and {year}. Example: {make}-{model}-{year}."
                                        />
                                    )}

                                    <Select
                                        label="Validation type"
                                        options={productTagValidationOptions}
                                        value={productTagValidationMode}
                                        onChange={(value) => setProductTagValidationMode(value as ProductTagValidationMode)}
                                        helpText="Create missing values during migration, or only import tags that already match existing app vehicle values."
                                    />
                                </BlockStack>
                            )}

                            {importFormat === 'atd_price_file' && (
                                <BlockStack gap="300">
                                    <Select
                                        label="Price adjustment"
                                        options={priceAdjustmentOptions}
                                        onChange={(value) => setPriceAdjustmentMode(value as AtdPriceAdjustmentMode)}
                                        value={priceAdjustmentMode}
                                        helpText="The adjustment is applied to the file MAP before updating the Shopify variant price."
                                    />

                                    {priceAdjustmentMode !== 'none' && (
                                        <TextField
                                            label={priceAdjustmentMode === 'percent' ? 'Increase MAP by' : 'Increase MAP by fixed amount'}
                                            type="number"
                                            value={priceAdjustmentValue}
                                            onChange={setPriceAdjustmentValue}
                                            autoComplete="off"
                                            min={0}
                                            suffix={priceAdjustmentMode === 'percent' ? '%' : '$'}
                                        />
                                    )}

                                    <Checkbox
                                        label="Skip price updates where MAP is blank or zero"
                                        checked={skipZeroMapPrices}
                                        onChange={setSkipZeroMapPrices}
                                        helpText="Prevents a price file row with MAP 0 from setting a Shopify variant price to $0.00."
                                    />
                                </BlockStack>
                            )}

                            {uploadState.error && (
                                <Banner tone="critical" title="Upload validation failed">
                                    <p>{uploadState.error}</p>
                                </Banner>
                            )}

                            {uploadState.success && (
                                <Banner tone="success" title="Import queued">
                                    <p>{uploadState.success}</p>
                                </Banner>
                            )}

                            {isSubmitting && uploadProgressPercent !== undefined && (
                                <BlockStack gap="100">
                                    <Text as="p" variant="bodySm" tone="subdued">Uploading file… {uploadProgressPercent}%</Text>
                                    <ProgressBar progress={uploadProgressPercent} size="small" />
                                </BlockStack>
                            )}

                            <InlineStack align="end">
                                <Button
                                    variant="primary"
                                    onClick={handlePrimaryImportAction}
                                    loading={isSubmitting || isPreviewingCsv}
                                    disabled={importFormat === 'csv_xlsx' && csvPreviewIsCurrent && csvPreview?.canImport === false}
                                >
                                    {importFormat === 'product_tags'
                                        ? 'Prepare preview'
                                        : importFormat === 'csv_xlsx'
                                            ? csvPreviewCanSubmit ? 'Review and submit' : 'Review file'
                                            : 'Submit File'}
                                </Button>
                            </InlineStack>
                        </FormLayout>
                    </Card>
                    ) : (
                    <Card>
                        <BlockStack gap="300">
                            <BlockStack gap="100">
                                <Text as="h2" variant="headingMd">Fitment export</Text>
                                <Text as="p" tone="subdued">Export your current product fitment assignments to a CSV file.</Text>
                            </BlockStack>

                            {exportState.error && (
                                <Banner tone="critical" title="Export failed">
                                    <p>{exportState.error}</p>
                                </Banner>
                            )}

                            {exportState.success && (
                                <Banner tone="success" title="Export queued">
                                    <p>{exportState.success}</p>
                                </Banner>
                            )}

                            <InlineStack align="start">
                                <Button variant="primary" onClick={handleQueueExport} loading={isQueueingExport}>Export fitments</Button>
                            </InlineStack>
                        </BlockStack>
                    </Card>
                    )}
                </Layout.Section>

                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between">
                                <Text as="h2" variant="headingMd">{historyTitle}</Text>
                                <Button onClick={loadHistory} loading={isLoadingHistory}>Refresh</Button>
                            </InlineStack>

                            {historyError && (
                                <Banner tone="critical" title={historyErrorTitle || loadHistoryErrorTitle}>
                                    <p>{historyError}</p>
                                </Banner>
                            )}

                            {visibleHistory.length === 0 && !isLoadingHistory ? (
                                <Text as="p" tone="subdued">{emptyHistoryText}</Text>
                            ) : (
                                <BlockStack gap="200">
                                    <ImportHistoryCards
                                        items={visibleHistory.slice(0, 5)}
                                        archivingIds={archivingIds}
                                        downloadingIds={downloadingIds}
                                        downloadingReviewIds={downloadingReviewIds}
                                        reviewingProductTagIds={reviewingProductTagIds}
                                        undoingProductTagIds={undoingProductTagIds}
                                        onArchive={handleArchive}
                                        onDownload={handleDownload}
                                        onDownloadReview={handleDownloadReview}
                                        onReviewProductTags={handleReviewProductTags}
                                        onUndoProductTags={handleUndoProductTags}
                                    />
                                    <Box paddingBlockStart="200">
                                        <InlineStack align="center">
                                            <Link to="/imports/history">View all history</Link>
                                        </InlineStack>
                                    </Box>
                                </BlockStack>
                            )}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>

            <Modal
                open={csvPreviewIsCurrent && isCsvPreviewModalOpen}
                onClose={handleCloseCsvPreviewModal}
                title={csvPreview?.canImport ? 'Review CSV/XLSX import' : 'CSV/XLSX preview needs attention'}
                primaryAction={{
                    content: csvXlsxImportMode === 'replace_matched_skus'
                        ? 'Submit and replace fitments'
                        : 'Submit file',
                    onAction: handleSubmit,
                    loading: isSubmitting,
                    disabled: !csvPreviewCanSubmit || isPreviewingCsv,
                }}
                secondaryActions={[
                    {
                        content: 'Back to upload',
                        onAction: handleCloseCsvPreviewModal,
                    },
                    {
                        content: 'Cancel import',
                        onAction: handleCancelCsvImport,
                        destructive: true,
                    },
                ]}
            >
                <Modal.Section>
                    {csvPreviewIsCurrent && csvPreview ? (
                        <BlockStack gap="300">
                            <Banner tone={csvPreview.canImport ? 'success' : 'warning'} title={csvPreview.canImport ? 'CSV/XLSX preview ready' : 'CSV/XLSX preview needs attention'}>
                                <p>{csvPreview.canImport ? 'Confirm the mapped columns and sample rows, then submit the import.' : 'The file was scanned, but required columns are missing.'}</p>
                            </Banner>

                            <InlineStack gap="400" wrap>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    File: {csvPreview.fileName || uploadState.file?.name || 'Selected file'}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Rows scanned: {csvPreview.scannedDataRowCount.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Mode: {csvXlsxImportMode === 'replace_matched_skus' ? 'Replace matched SKUs' : 'Append'}
                                </Text>
                            </InlineStack>

                            {csvXlsxImportMode === 'replace_matched_skus' ? (
                                <Banner tone="warning" title="Replace mode selected">
                                    <p>Existing fitments will be cleared only for variants whose SKU is matched by this file.</p>
                                </Banner>
                            ) : null}

                            {csvPreview.errors?.length ? (
                                <Banner tone="critical" title="Preview errors">
                                    <ul>
                                        {csvPreview.errors.map((error) => <li key={error}>{error}</li>)}
                                    </ul>
                                </Banner>
                            ) : null}

                            {csvPreview.warnings?.length ? (
                                <Banner tone="info" title="Preview notes">
                                    <ul>
                                        {csvPreview.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                                    </ul>
                                </Banner>
                            ) : null}

                            <BlockStack gap="200">
                                <Text as="h3" variant="headingSm">Column mapping</Text>
                                <CsvPreviewTable
                                    headings={['Import field', 'File column', 'Requirement']}
                                    rows={csvColumnMappingRows}
                                />
                            </BlockStack>

                            {csvPreviewRows.length > 0 ? (
                                <BlockStack gap="200">
                                    <Text as="h3" variant="headingSm">Sample rows</Text>
                                    <CsvPreviewTable
                                        headings={csvPreviewHeadings}
                                        rows={csvPreviewRows}
                                    />
                                    {csvPreview.hasMoreRows ? (
                                        <Text as="p" tone="subdued">Showing the first {csvPreview.rows.length} data rows.</Text>
                                    ) : null}
                                </BlockStack>
                            ) : null}
                        </BlockStack>
                    ) : null}
                </Modal.Section>
            </Modal>

            <Modal
                open={!!acesSetup}
                onClose={handleCloseAcesModal}
                title="Finalize ACES import"
                primaryAction={{
                    content: 'Start ACES import',
                    onAction: handleConfirmAcesSetup,
                    loading: isConfirmingAcesSetup,
                }}
                secondaryActions={[
                    {
                        content: 'Cancel',
                        onAction: handleCloseAcesModal,
                    },
                ]}
            >
                <Modal.Section>
                    <BlockStack gap="300">
                        <Text as="p" tone="subdued">Confirm vendor mapping before fitment rows are processed.</Text>

                        <Text as="p" variant="bodySm">{acesSetup?.details.documentTitle || 'Untitled ACES document'} {acesSetup?.details.company ? `· ${acesSetup.details.company}` : ''}</Text>

                        <Select
                            label="Vendor (optional)"
                            options={[
                                { label: 'No vendor (match store-wide)', value: noVendorOptionValue },
                                ...(acesSetup?.details.vendorOptions.map((v) => ({ label: v, value: v })) ?? []),
                            ]}
                            value={acesSetup?.selectedVendor || noVendorOptionValue}
                            onChange={(value) => setAcesSetup((previous) => {
                                if (!previous) {
                                    return previous;
                                }

                                const selectedVendor = value === noVendorOptionValue ? '' : value;

                                return {
                                    ...previous,
                                    selectedVendor,
                                    skuSuffixRule: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.skuPrefix ?? '') : '',
                                    useSkuSuffixRule: selectedVendor ? previous.useSkuSuffixRule : false,
                                    updateVendorRule: selectedVendor ? previous.updateVendorRule : false,
                                    normalizeSku: selectedVendor ? (vendorSuffixRulesByVendor[selectedVendor]?.normalizeSku ?? false) : false,
                                };
                            })}
                            helpText="Leave vendor unselected to match SKUs across the entire store."
                        />

                        <TextField
                            label="Vendor SKU suffix rule"
                            value={acesSetup?.skuSuffixRule ?? ''}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, skuSuffixRule: value } : previous)}
                            autoComplete="off"
                            disabled={!acesSetup?.selectedVendor}
                            helpText="Adds this suffix when matching ACES part numbers to Shopify SKUs. Falls back to the original part number if no match is found."
                        />

                        <Checkbox
                            label="Use this suffix rule for this import"
                            checked={acesSetup?.useSkuSuffixRule ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, useSkuSuffixRule: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />

                        <Checkbox
                            label="Normalize SKU (remove special characters before matching)"
                            checked={acesSetup?.normalizeSku ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, normalizeSku: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />

                        <Checkbox
                            label="Update saved vendor rule with this value"
                            checked={acesSetup?.updateVendorRule ?? false}
                            onChange={(value) => setAcesSetup((previous) => previous ? { ...previous, updateVendorRule: value } : previous)}
                            disabled={!acesSetup?.selectedVendor}
                        />
                    </BlockStack>
                </Modal.Section>
            </Modal>

            <Modal
                open={!!productTagPreview}
                onClose={() => {
                    if (!productTagPreview || approvingProductTagIds[productTagPreview.queueId]) {
                        return;
                    }

                    setProductTagPreview(undefined);
                    setProductTagPreviewError(undefined);
                }}
                title="Review Product Tags import"
                primaryAction={productTagPreview?.awaitingApproval ? {
                    content: 'Apply import',
                    onAction: handleApproveProductTags,
                    loading: productTagPreview ? !!approvingProductTagIds[productTagPreview.queueId] : false,
                    disabled: !productTagPreview || productTagPreview.willApplyRows === 0,
                } : undefined}
                secondaryActions={[
                    {
                        content: 'Close',
                        onAction: () => {
                            setProductTagPreview(undefined);
                            setProductTagPreviewError(undefined);
                        },
                    },
                ]}
            >
                <Modal.Section>
                    {productTagPreview && (
                        <BlockStack gap="300">
                            {productTagPreviewError && (
                                <Banner tone="critical" title="Product Tags import action failed">
                                    <p>{productTagPreviewError}</p>
                                </Banner>
                            )}

                            <InlineStack gap="400" wrap>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Products: {productTagPreview.productCount.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Ready rows: {productTagPreview.willApplyRows.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    New vehicle values: {productTagPreview.willCreateFitmentRecords.toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Skipped: {productTagPreview.skippedRows.toLocaleString()}
                                </Text>
                            </InlineStack>

                            <Text as="p" variant="bodySm" tone="subdued">
                                These rows are staged only. The app source of truth is not changed until you apply the import.
                            </Text>

                            {productTagPreviewRows.length > 0 ? (
                                <DataTable
                                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                                    headings={['Product', 'SKU', 'Tag', 'Fitment', 'Action']}
                                    rows={productTagPreviewRows}
                                />
                            ) : (
                                <Text as="p" tone="subdued">No preview rows were created.</Text>
                            )}

                            {productTagPreview.totalRows > productTagPreview.rows.length && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Showing {productTagPreview.rows.length.toLocaleString()} of {productTagPreview.totalRows.toLocaleString()} staged row(s).
                                </Text>
                            )}
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>
        </Page>
    );
};

export default ImportsPage;
